/**
 * Strategy Marketplace Page
 * Fast, lightweight, feature-rich marketplace
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  getStrategies,
  getFeaturedStrategies,
  getTrendingStrategies,
  getNewReleases,
  searchStrategies,
  getStrategy,
  getStrategyRatings,
  cloneStrategy,
  rateStrategy,
  getMarketplaceStats,
  type MarketplaceStrategy,
  type StrategyRating,
  type MarketplaceStats,
  type SearchFilters,
  CATEGORIES,
  TIMEFRAMES,
  PRICING_MODELS,
  SORT_OPTIONS,
  formatPrice,
  formatMetric,
  getRatingStars,
  clearCache,
} from '../services/marketplaceApi'
import LoadingSpinner from '../components/LoadingSpinner'
import WishlistButton from '../components/WishlistButton'
import CompareBar from '../components/CompareBar'
import MarketplaceFAQ from '../components/MarketplaceFAQ'

// ============================================================================
// Components
// ============================================================================

const StarRating = ({ rating, size = 16 }: { rating: number; size?: number }) => {
  const { full, half, empty } = getRatingStars(rating)
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(full)].map((_, i) => (
        <svg key={`full-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#f59e0b">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {half && (
        <svg width={size} height={size} viewBox="0 0 20 20">
          <defs>
            <linearGradient id="half">
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="50%" stopColor="#e5e7eb" />
            </linearGradient>
          </defs>
          <path fill="url(#half)" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
      {[...Array(empty)].map((_, i) => (
        <svg key={`empty-${i}`} width={size} height={size} viewBox="0 0 20 20" fill="#e5e7eb">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  )
}

const MetricCard = ({ label, value, trend }: { label: string; value: string; trend?: 'up' | 'down' }) => (
  <div className="bg-gray-800/50 rounded-lg p-3">
    <div className="text-xs text-gray-400">{label}</div>
    <div className={`text-lg font-bold ${trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-white'}`}>
      {value}
      {trend === 'up' && <span className="ml-1 text-xs">↑</span>}
      {trend === 'down' && <span className="ml-1 text-xs">↓</span>}
    </div>
  </div>
)

const StrategyCard = ({ 
  strategy, 
  onClick,
  compareMode = false,
  isCompareSelected = false,
  onCompareToggle
}: { 
  strategy: MarketplaceStrategy
  onClick: () => void
  compareMode?: boolean
  isCompareSelected?: boolean
  onCompareToggle?: (id: string) => void
}) => {
  const pricing = PRICING_MODELS.find(p => p.value === strategy.pricing)
  const returnColor = strategy.metrics.total_return >= 0 ? 'text-green-400' : 'text-red-400'

  return (
    <div
      onClick={onClick}
      className={`bg-gray-800 rounded-xl overflow-hidden cursor-pointer hover:bg-gray-750 transition-all hover:scale-[1.02] hover:shadow-xl group ${
        isCompareSelected ? 'ring-2 ring-purple-500' : ''
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-start gap-2">
            {compareMode && (
              <input
                type="checkbox"
                checked={isCompareSelected}
                onChange={(e) => {
                  e.stopPropagation()
                  onCompareToggle?.(strategy.strategy_id)
                }}
                className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500 mt-1 cursor-pointer"
              />
            )}
            <div className="flex-1">
              {strategy.is_featured && (
                <span className="inline-block px-2 py-0.5 text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full mb-2">
                  ⭐ Featured
                </span>
              )}
              {strategy.is_premium && (
                <span className="inline-block px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-full ml-2">
                  Premium
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <WishlistButton strategyId={strategy.strategy_id} size="sm" />
            <span
              className="px-2 py-1 text-xs font-medium rounded-lg"
              style={{ backgroundColor: `${pricing?.color}20`, color: pricing?.color }}
            >
              {formatPrice(strategy.price, strategy.pricing)}
            </span>
          </div>
        </div>
        <h3 className="font-semibold text-white group-hover:text-primary transition-colors line-clamp-1">
          {strategy.name}
        </h3>
        <p className="text-sm text-gray-400 mt-1 line-clamp-2">{strategy.description}</p>
        <Link 
          to={`/creator/${strategy.creator_id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-primary hover:text-primary-dark mt-2 inline-block"
        >
          by {strategy.creator_name}
        </Link>
      </div>

      {/* Metrics */}
      <div className="p-4 grid grid-cols-2 gap-3">
        <MetricCard label="Return" value={`${strategy.metrics.total_return >= 0 ? '+' : ''}${strategy.metrics.total_return.toFixed(1)}%`} trend={strategy.metrics.total_return >= 0 ? 'up' : 'down'} />
        <MetricCard label="Win Rate" value={`${strategy.metrics.win_rate.toFixed(1)}%`} />
        <MetricCard label="Sharpe" value={strategy.metrics.sharpe_ratio.toFixed(2)} />
        <MetricCard label="Trades" value={strategy.metrics.total_trades.toString()} />
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StarRating rating={strategy.avg_rating} />
          <span className="text-sm text-gray-400">({strategy.total_ratings})</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/strategy/${strategy.strategy_id}/performance`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
          >
            📊 Performance
          </Link>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <span>👁 {strategy.view_count}</span>
            <span>📋 {strategy.clone_count}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

const CategoryPill = ({ category, selected, onClick }: { category: typeof CATEGORIES[0]; selected: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
      selected
        ? 'bg-primary text-white shadow-lg shadow-primary/25'
        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
    }`}
  >
    <span className="mr-1">{category.icon}</span>
    {category.label}
  </button>
)

const FilterDropdown = ({ options, value, onChange, label }: {
  options: readonly { value: string; label: string }[]
  value: string
  onChange: (v: string) => void
  label: string
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-gray-800 text-white px-3 py-2 rounded-lg text-sm border border-gray-700 focus:border-primary outline-none"
  >
    <option value="">{label}</option>
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
)

// ============================================================================
// Main Component
// ============================================================================

export default function Marketplace() {
  // State
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'browse' | 'featured' | 'trending' | 'new'>('browse')
  const [strategies, setStrategies] = useState<MarketplaceStrategy[]>([])
  const [stats, setStats] = useState<MarketplaceStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedStrategy, setSelectedStrategy] = useState<MarketplaceStrategy | null>(null)
  const [strategyRatings, setStrategyRatings] = useState<StrategyRating[]>([])
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [compareMode, setCompareMode] = useState(false)
  const [compareSelected, setCompareSelected] = useState<string[]>([])

  // Filters
  const [filters, setFilters] = useState<SearchFilters>({
    sort_by: 'popular',
    category: '',
    pricing: '',
  })
  const [searchQuery, setSearchQuery] = useState('')

  // Rating state
  const [userRating, setUserRating] = useState(0)
  const [reviewText, setReviewText] = useState('')

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      if (activeTab === 'featured') {
        const res = await getFeaturedStrategies(12)
        if (res.success && res.data) setStrategies(res.data)
      } else if (activeTab === 'trending') {
        const res = await getTrendingStrategies(12)
        if (res.success && res.data) setStrategies(res.data)
      } else if (activeTab === 'new') {
        const res = await getNewReleases(12)
        if (res.success && res.data) setStrategies(res.data)
      } else {
        const res = await getStrategies(filters, 24, 0)
        if (res.success && res.data) setStrategies(res.data)
      }

      // Load stats once
      if (!stats) {
        const statsRes = await getMarketplaceStats()
        if (statsRes.success && statsRes.data) setStats(statsRes.data)
      }
    } finally {
      setLoading(false)
    }
  }, [activeTab, filters, stats])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Search handler
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      loadData()
      return
    }
    setLoading(true)
    const res = await searchStrategies({ ...filters, query: searchQuery })
    if (res.success && res.data) setStrategies(res.data)
    setLoading(false)
  }, [searchQuery, filters, loadData])

  // Open strategy detail
  const openStrategyDetail = async (strategy: MarketplaceStrategy) => {
    setSelectedStrategy(strategy)
    setShowDetailModal(true)
    const ratingsRes = await getStrategyRatings(strategy.strategy_id)
    if (ratingsRes.success && ratingsRes.data) {
      setStrategyRatings(ratingsRes.data)
    }
  }

  // Clone strategy
  const handleClone = async () => {
    if (!selectedStrategy) return
    setCloning(true)
    const res = await cloneStrategy(selectedStrategy.strategy_id)
    setCloning(false)
    if (res.success) {
      alert('Strategy cloned successfully! Check your strategies.')
      setShowDetailModal(false)
    } else {
      alert(res.error || 'Failed to clone strategy')
    }
  }

  // Submit rating
  const handleSubmitRating = async () => {
    if (!selectedStrategy || userRating === 0) return
    await rateStrategy(selectedStrategy.strategy_id, userRating, reviewText)
    setShowRatingModal(false)
    setUserRating(0)
    setReviewText('')
    // Reload ratings
    const ratingsRes = await getStrategyRatings(selectedStrategy.strategy_id)
    if (ratingsRes.success && ratingsRes.data) {
      setStrategyRatings(ratingsRes.data)
    }
  }

  // Tabs
  const tabs = [
    { id: 'browse', label: 'Browse All', icon: '📚' },
    { id: 'featured', label: 'Featured', icon: '⭐' },
    { id: 'trending', label: 'Trending', icon: '🔥' },
    { id: 'new', label: 'New', icon: '✨' },
    { id: 'faq', label: 'FAQ', icon: '❓' },
  ] as const

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 via-purple-900/10 to-pink-900/10 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <span className="text-4xl">🎯</span>
                Strategy Marketplace
              </h1>
              <p className="text-gray-400 mt-2">
                Discover, clone, and share trading strategies
              </p>
            </div>

            {/* Stats */}
            {stats && (
              <div className="flex gap-4 flex-wrap">
                <div className="bg-gray-800/50 rounded-lg px-4 py-2">
                  <div className="text-xs text-gray-400">Strategies</div>
                  <div className="text-xl font-bold text-white">{stats.total_strategies}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg px-4 py-2">
                  <div className="text-xs text-gray-400">Creators</div>
                  <div className="text-xl font-bold text-white">{stats.total_creators}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg px-4 py-2">
                  <div className="text-xs text-gray-400">Clones</div>
                  <div className="text-xl font-bold text-primary">{stats.total_clones}</div>
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="mt-6 flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search strategies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-xl border border-gray-700 focus:border-primary outline-none pl-10"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-xl transition-colors"
            >
              Search
            </button>
            <button
              onClick={() => { clearCache(); loadData() }}
              className="px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors"
            >
              🔄
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-primary text-white shadow-lg shadow-primary/25'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            onClick={() => setCompareMode(!compareMode)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
              compareMode
                ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <span className="mr-2">⚖️</span>
            Compare
            {compareSelected.length > 0 && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                {compareSelected.length}
              </span>
            )}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <FilterDropdown
            options={CATEGORIES}
            value={filters.category || ''}
            onChange={(v) => setFilters({ ...filters, category: v })}
            label="Category"
          />
          <FilterDropdown
            options={TIMEFRAMES}
            value={filters.timeframe || ''}
            onChange={(v) => setFilters({ ...filters, timeframe: v })}
            label="Timeframe"
          />
          <FilterDropdown
            options={PRICING_MODELS}
            value={filters.pricing || ''}
            onChange={(v) => setFilters({ ...filters, pricing: v })}
            label="Pricing"
          />
          <FilterDropdown
            options={SORT_OPTIONS}
            value={filters.sort_by || 'popular'}
            onChange={(v) => setFilters({ ...filters, sort_by: v as SearchFilters['sort_by'] })}
            label="Sort By"
          />
          {(filters.category || filters.pricing || filters.timeframe) && (
            <button
              onClick={() => setFilters({ sort_by: 'popular' })}
              className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {CATEGORIES.map(cat => (
            <CategoryPill
              key={cat.value}
              category={cat}
              selected={filters.category === cat.value}
              onClick={() => setFilters({
                ...filters,
                category: filters.category === cat.value ? '' : cat.value
              })}
            />
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : strategies.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-white mb-2">No strategies found</h3>
            <p className="text-gray-400">Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {strategies.map(strategy => (
              <StrategyCard
                key={strategy.strategy_id}
                strategy={strategy}
                onClick={() => openStrategyDetail(strategy)}
                compareMode={compareMode}
                isCompareSelected={compareSelected.includes(strategy.strategy_id)}
                onCompareToggle={(id) => {
                  if (compareSelected.includes(id)) {
                    setCompareSelected(prev => prev.filter(x => x !== id))
                  } else if (compareSelected.length < 3) {
                    setCompareSelected(prev => [...prev, id])
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* FAQ Tab Content */}
      {activeTab === 'faq' && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <MarketplaceFAQ />
        </div>
      )}

      {/* Compare Bar */}
      {activeTab !== 'faq' && (
        <CompareBar 
          selectedIds={compareSelected} 
          onClear={() => {
            setCompareSelected([])
            setCompareMode(false)
          }} 
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedStrategy && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex justify-between items-start">
              <div>
                <div className="flex gap-2 mb-2">
                  <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                    {CATEGORIES.find(c => c.value === selectedStrategy.category)?.label || selectedStrategy.category}
                  </span>
                  <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">
                    {selectedStrategy.timeframe}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-white">{selectedStrategy.name}</h2>
                <p 
                  className="text-primary hover:text-primary-dark mt-1 cursor-pointer transition-colors"
                  onClick={() => navigate(`/creator/${selectedStrategy.creator_id}`)}
                >
                  by {selectedStrategy.creator_name}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <WishlistButton strategyId={selectedStrategy.strategy_id} size="md" />
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Price & Rating */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-bold text-white">
                    {formatPrice(selectedStrategy.price, selectedStrategy.pricing)}
                  </span>
                  {selectedStrategy.pricing === 'subscription' && (
                    <span className="text-gray-400 text-sm">/month</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StarRating rating={selectedStrategy.avg_rating} size={20} />
                  <span className="text-white font-medium">{selectedStrategy.avg_rating.toFixed(1)}</span>
                  <span className="text-gray-400">({selectedStrategy.total_ratings} reviews)</span>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">Description</h3>
                <p className="text-gray-300">{selectedStrategy.description}</p>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-900 rounded-xl p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Total Return</div>
                  <div className={`text-2xl font-bold ${selectedStrategy.metrics.total_return >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedStrategy.metrics.total_return >= 0 ? '+' : ''}{selectedStrategy.metrics.total_return.toFixed(1)}%
                  </div>
                </div>
                <div className="bg-gray-900 rounded-xl p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Win Rate</div>
                  <div className="text-2xl font-bold text-white">{selectedStrategy.metrics.win_rate.toFixed(1)}%</div>
                </div>
                <div className="bg-gray-900 rounded-xl p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Sharpe Ratio</div>
                  <div className="text-2xl font-bold text-white">{selectedStrategy.metrics.sharpe_ratio.toFixed(2)}</div>
                </div>
                <div className="bg-gray-900 rounded-xl p-4 text-center">
                  <div className="text-sm text-gray-400 mb-1">Max Drawdown</div>
                  <div className="text-2xl font-bold text-red-400">{selectedStrategy.metrics.max_drawdown.toFixed(1)}%</div>
                </div>
              </div>

              {/* Indicators */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">Indicators</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedStrategy.indicators.map(ind => (
                    <span key={ind} className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">
                      {ind}
                    </span>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedStrategy.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-gray-700/50 text-gray-400 rounded-full text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Reviews */}
              {strategyRatings.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Reviews</h3>
                  <div className="space-y-4 max-h-60 overflow-y-auto">
                    {strategyRatings.slice(0, 5).map(rating => (
                      <div key={rating.rating_id} className="bg-gray-900 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{rating.username}</span>
                            {rating.is_verified_purchase && (
                              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Verified</span>
                            )}
                          </div>
                          <StarRating rating={rating.rating} size={14} />
                        </div>
                        {rating.review && <p className="text-gray-300 text-sm">{rating.review}</p>}
                        {(rating.pros.length > 0 || rating.cons.length > 0) && (
                          <div className="flex gap-4 mt-2 text-sm">
                            {rating.pros.length > 0 && (
                              <div>
                                <span className="text-green-400">✓</span> {rating.pros.join(', ')}
                              </div>
                            )}
                            {rating.cons.length > 0 && (
                              <div>
                                <span className="text-red-400">✗</span> {rating.cons.join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={handleClone}
                  disabled={cloning}
                  className="flex-1 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                  {cloning ? 'Cloning...' : selectedStrategy.pricing === 'free' ? 'Clone Strategy' : `Buy for ${formatPrice(selectedStrategy.price, selectedStrategy.pricing)}`}
                </button>
                <button
                  onClick={() => setShowRatingModal(true)}
                  className="px-6 py-4 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  ⭐ Rate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowRatingModal(false)}>
          <div className="bg-gray-800 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-4">Rate This Strategy</h3>
            
            {/* Star Input */}
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setUserRating(star)}
                  className="text-4xl transition-transform hover:scale-110"
                >
                  <span className={star <= userRating ? 'text-yellow-400' : 'text-gray-600'}>★</span>
                </button>
              ))}
            </div>

            {/* Review */}
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience (optional)"
              className="w-full bg-gray-900 text-white p-4 rounded-xl border border-gray-700 focus:border-primary outline-none resize-none h-32 mb-4"
            />

            <div className="flex gap-4">
              <button
                onClick={() => setShowRatingModal(false)}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRating}
                disabled={userRating === 0}
                className="flex-1 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
              >
                Submit Rating
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
